import { useEffect, type CSSProperties } from 'react'
import { C, grad, CAL, TrialCta, SUPPORT_EMAIL } from '../components/shared'

/*
 * /pricing (Launch fixes 1, 2026-10-19). The plans are the app's own list, seo-platform
 * src/services/billingPlans.ts: the trial (14 days, 1 site, 3 tool runs for the whole trial, review and download
 * only), Execute ($299, 1 site), Execute Plus ($499, 3 sites), Agency ($999, 15 sites), 20 tool runs a site a month,
 * pooled. Change a price or a limit there first, then here.
 */

const PLANS = [
  {
    name: 'Trial',
    price: 'Free',
    period: 'for 14 days',
    lines: ['1 site', '3 tool runs for the whole trial', 'Review and download each change', 'Publishing needs a plan', 'No card needed'],
    trial: true,
  },
  {
    name: 'Execute',
    price: '$299',
    period: 'a month',
    lines: ['1 site', '20 tool runs a month', 'Publishing each change after you approve it'],
  },
  {
    name: 'Execute Plus',
    price: '$499',
    period: 'a month',
    lines: ['3 sites', '60 tool runs a month, shared across your sites', 'Publishing each change after you approve it'],
  },
  {
    name: 'Agency',
    price: '$999',
    period: 'a month',
    lines: ['15 sites', '300 tool runs a month, shared across your sites', 'Publishing each change after you approve it'],
  },
] as const

const card: CSSProperties = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: '28px 24px',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 12px 36px rgba(0,0,0,.04)',
}

export default function PricingPage() {
  useEffect(() => {
    document.title = 'Pricing | SerpDesk'
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'SerpDesk plans: a free 14-day trial, then Execute $299, Execute Plus $499 or Agency $999 a month.')
  }, [])
  return (
    <>
      <section style={{ background: C.hero, color: '#fff', paddingTop: 125, paddingBottom: 56 }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 'clamp(30px, 5vw, 48px)', fontWeight: 800, letterSpacing: '-.03em', margin: 0 }}>Pricing</h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,.6)', lineHeight: 1.65, maxWidth: 620, marginTop: 14 }}>Start with a free 14-day trial. Every change waits for your approval before anything is published.</p>
        </div>
      </section>

      <section style={{ background: C.warm, padding: '48px 0 72px' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20 }}>
            {PLANS.map((p) => (
              <div key={p.name} style={{ ...card, ...('trial' in p ? { borderColor: C.purpleSoft } : {}) }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: C.purple }}>{p.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '14px 0 18px' }}>
                  <span style={{ fontFamily: 'var(--display)', fontSize: 38, fontWeight: 800, color: C.ink, letterSpacing: '-.03em' }}>{p.price}</span>
                  <span style={{ fontSize: 13.5, color: C.muted }}>{p.period}</span>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
                  {p.lines.map((l) => (
                    <li key={l} style={{ display: 'flex', gap: 10, fontSize: 14, color: C.body, lineHeight: 1.5 }}>
                      <span style={{ color: C.purple, flexShrink: 0 }}>✓</span>{l}
                    </li>
                  ))}
                </ul>
                {'trial' in p
                  ? <TrialCta variant="card" />
                  : <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, margin: 0 }}>Choose it on the billing page in the app, during or after your trial.</p>}
              </div>
            ))}
          </div>

          <div style={{ maxWidth: 760, margin: '40px auto 0', fontSize: 14, color: C.body, lineHeight: 1.75 }}>
            <p style={{ margin: '0 0 10px' }}>A tool run is one run of a SerpDesk tool on your site, for example an update to one page or one new post. Paid plans are billed monthly in advance through Stripe. You can cancel at any time; your plan stays active until the end of the period you paid for. See <a href="/refund" style={{ color: C.purple }}>refunds and cancellation</a> and the <a href="/terms" style={{ color: C.purple }}>terms</a>.</p>
            <p style={{ margin: 0 }}>Questions: <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: C.purple }}>{SUPPORT_EMAIL}</a>.</p>
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <a href={CAL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 15, fontWeight: 700, color: '#fff', padding: '14px 30px', borderRadius: 12, background: grad, textDecoration: 'none', display: 'inline-block' }}>Book a Preloaded Demo →</a>
          </div>
        </div>
      </section>
    </>
  )
}
